import { useBrandhubPressReleasesQuery } from '@brandhub/hooks/brandhubQueries';
import { useCurrentBrandHubDomainId } from '@brandhub/hooks/useCurrentBrandHubDomainId';
import FileUploader from '@components/FileUploader';
import WarningM from '@semcore/icon/Warning/m';
import { ButtonLink } from '@semcore/ui/button';
import Spin from '@semcore/ui/spin';
import Tooltip from '@semcore/ui/tooltip';
import { Text } from '@semcore/ui/typography';
import { useHasMultipleBrandsAccess } from '@src/hooks/useHasMultipleBrandsAccess';
import { FileUploaderProps, UploadStatus } from '@src/types/files-upload-types';
import { useTranslation } from 'react-i18next';

interface AnnouncementObjectivesFormFileUploadStatusProps {
  uploadStatus: UploadStatus;
  fileUploaderProps: FileUploaderProps;
  onSelectFromBrandHub: () => void;
}

export const AnnouncementObjectivesFormFileUploadStatus = ({
  uploadStatus,
  fileUploaderProps,
  onSelectFromBrandHub,
}: AnnouncementObjectivesFormFileUploadStatusProps) => {
  const { t } = useTranslation(['common', 'mailings']);

  const hasMultipleBrandsAccess = useHasMultipleBrandsAccess();
  const [brandHubDomainId] = useCurrentBrandHubDomainId();

  const { data } = useBrandhubPressReleasesQuery({
    limit: 1,
    page: 1,
    ...(hasMultipleBrandsAccess && { brandHubDomainId }),
  });

  switch (uploadStatus) {
    case 'uploading':
      return (
        <div className="flex gap-1">
          <Spin size="xs" />
          <Text>
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.fileUpload.uploadInProgress',
            )}
          </Text>
        </div>
      );

    case 'error':
      return (
        <div className="flex items-center gap-1.5">
          <Text
            className="flex items-center gap-1.5"
            color="text-critical">
            <WarningM />
            {t('mailings:personalizedMailingCreatorWizard.objectivesForm.fileUpload.uploadFailed')}
          </Text>
          <FileUploader {...fileUploaderProps}>
            <ButtonLink
              data-test="re-upload-file-button"
              size={300}
              use="primary">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.fileUpload.uploadFailedTryAgain',
              )}
            </ButtonLink>
          </FileUploader>
        </div>
      );

    default:
      return (
        <div className="flex gap-1">
          <FileUploader {...fileUploaderProps}>
            <ButtonLink
              data-test="upload-document-link"
              size={300}
              use="primary">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyLink.uploadPressRelease',
              )}
            </ButtonLink>
          </FileUploader>
          {t('common:or')}
          <Tooltip
            interaction={data?.pressReleases.length === 0 ? 'hover' : 'none'}
            title={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyLink.noPressReleasesInWorkspace',
            )}>
            <ButtonLink
              data-test="select-from-workspace-button"
              disabled={!data?.pressReleases.length}
              size={300}
              use="primary"
              onClick={onSelectFromBrandHub}>
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyLink.selectFromWorkspace',
              )}
            </ButtonLink>
          </Tooltip>
        </div>
      );
  }
};
